/**
 * Get Responsive Values Utility
 *
 * Utility function for getting values for a specific breakpoint from responsive attributes
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import type { ResponsiveAttributes, ResponsiveBreakpoint } from '../../types';

/**
 * Get values for a specific breakpoint from responsive attributes
 */
export const getResponsiveValues = < T = Record<string, unknown> >(
	attributes: ResponsiveAttributes< T >,
	breakpoint: ResponsiveBreakpoint,
): T => {
	/**
     * Inherit values following the Desktop → Tablet → Mobile cascade.
     * We always return an object so the consumer can safely destructure
     * without additional undefined checks.
     */

	// Helper to safely clone an object or fallback to an empty one
	const clone = ( value: T | undefined ): T => {
		return ( value ? { ...value } : {} ) as T;
	};

	const desktopValues = clone( attributes?.desktop );
	const tabletValues = clone( attributes?.tablet );
	const mobileValues = clone( attributes?.mobile );

	switch ( breakpoint ) {
		case 'desktop':
			return desktopValues;
		case 'tablet':
			return {
				...desktopValues,
				...tabletValues,
			} as T;
		case 'mobile':
			return {
				...desktopValues,
				...tabletValues,
				...mobileValues,
			} as T;
		default:
			// Fallback to desktop values for safety
			return desktopValues;
	}
};
